import { gql } from '@apollo/client';
import axios from 'axios';
import { MASTERDATA_FILE_UPLOAD_URL } from './constants';
import { MasterdataConfig } from './types';
import { isArray } from 'lodash';

export const GET_PAGINATED_DATA_IMPORT_RUNS = gql`
    query PaginatedDataImportRuns($filters: [TableFilter], $offset: Int, $first: Int) {
        dataImportRuns(filters: $filters, offset: $offset, first: $first) {
            totalCount
            edges {
                node {
                    id
                    customerId
                    lookupType
                    lookupDefinitionId
                    status
                    isActiveVersion
                    trigger
                    inputFileUrl
                    inputFileSize
                    inputFileNumRows
                    inputFileName
                    inputFile2Url
                    inputFile2Size
                    inputFile2NumRows
                    inputFile2Name
                    inputFileUrl
                    ingestionFileUrl
                    ingestionFileName
                    ingestionFileSize
                    ingestionFileNumRows
                    errorType
                    errorMessage
                    errorMessageKwargs
                    createdAt
                    startedAt
                    finishedAt
                    djangoAdminUrl
                }
            }
        }
    }
`;

// Slim variant of GET_PAGINATED_DATA_IMPORT_RUNS for places that only need counts and per-definition
// summary stats (overview table, detail page general tab). Deliberately reuses the operation name
// "PaginatedDataImportRuns": the backend gateway routes GraphQL requests to the metadata service
// by operation name (outsmart-backend gateway/middleware.py).
export const GET_PAGINATED_DATA_IMPORT_RUN_SUMMARIES = gql`
    query PaginatedDataImportRuns($filters: [TableFilter], $offset: Int, $first: Int) {
        dataImportRuns(filters: $filters, offset: $offset, first: $first) {
            totalCount
            edges {
                node {
                    id
                    lookupDefinitionId
                    finishedAt
                    inputFileNumRows
                    ingestionFileNumRows
                }
            }
        }
    }
`;

export const uploadMasterdataFiles = (files: any[], lookupDefinitionId: string | string[]) => {
    return axios.all(
        files.map((file) => {
            const formData = new FormData();
            formData.append(
                'lookup_definition_id',
                isArray(lookupDefinitionId) ? lookupDefinitionId.join(',') : lookupDefinitionId
            );
            formData.append('file_name', file.name);
            formData.append('file', file);

            return axios.post(MASTERDATA_FILE_UPLOAD_URL, formData, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
        })
    );
};

export const uploadArticleList = (files, masterDataConfig: MasterdataConfig) =>
    uploadMasterdataFiles(files, masterDataConfig.articleLookupDefinitionId);

export const uploadDebitorArticleList = (files, masterDataConfig: MasterdataConfig) =>
    uploadMasterdataFiles(files, masterDataConfig.debitorArticleLookupDefinitionId);

export const uploadClientList = (files, masterDataConfig: MasterdataConfig) =>
    uploadMasterdataFiles(files, masterDataConfig.clientLookupDefinitionId);

export const uploadReceiverList = (files, masterDataConfig: MasterdataConfig) =>
    uploadMasterdataFiles(files, masterDataConfig.receiverLookupDefinitionId);

export const uploadDeliveryAddressList = (files, masterDataConfig: MasterdataConfig) =>
    uploadMasterdataFiles(files, masterDataConfig.deliveryAddressLookupDefinitionId);

export const uploadInvoiceAddressList = (files, masterDataConfig: MasterdataConfig) =>
    uploadMasterdataFiles(files, masterDataConfig.invoiceAddressLookupDefinitionId);

export const uploadConversionFactorList = (files, masterDataConfig: MasterdataConfig) =>
    uploadMasterdataFiles(files, masterDataConfig.conversionFactorLookupDefinitionId);

export const uploadContactList = (files, masterDataConfig: MasterdataConfig) =>
    uploadMasterdataFiles(files, masterDataConfig.contactLookupDefinitionId);

export const uploadOrderMatchingList = (files, masterDataConfig: MasterdataConfig) =>
    uploadMasterdataFiles(files, masterDataConfig.orderMatchingLookupDefinitionId);

export const uploadOfferMatchingList = (files, masterDataConfig: MasterdataConfig) =>
    uploadMasterdataFiles(files, masterDataConfig.offerMatchingLookupDefinitionId);

export const BASE_MASTERDATA_ENTITY = `
    id
    partitionId
    versionId
    lookupDefinitionId
    contentSummaryMultiline
    extraData
`;

export const CLIENT_MASTERDATA_ENTITY = `
    ${BASE_MASTERDATA_ENTITY}
    clientId1
    clientId2
    clientId3
    clientType
    clientContactPerson1
    companyName
    misc
    address1
    streetName
    streetNumber
    zipCode
    city
    countrySubdivision
    country
    iln
    vat
    locale
    localeNumeric
    internalNote
`;

export const CONVERSION_FACTOR_MASTERDATA_ENTITY = `
    ${BASE_MASTERDATA_ENTITY}
    sourceUnit
    targetUnit
    articleId
    conversionQuotient
`;

export const ARTICLE_MASTERDATA_ENTITY = `
    ${BASE_MASTERDATA_ENTITY}
    articleId1
    articleId2
    articleId3
    articlePartitionSpecificNumber
    articleStatus
    description
    orderUnits
    salesUnits
    grossPrice
    variants {
        dimensions {
            name
            values
        }
    }
    orderUnitConversionTarget
    orderUnitConversionFactor
    orderUnitConversionSource
    salesOrganisation
    salesOrganisationOptions
`;

export const ADDRESS_MASTERDATA_ENTITY = `
    ${BASE_MASTERDATA_ENTITY}
    addressId1
    addressId2
    addressId3
    contactPerson
    name
    misc
    address1
    address2
    streetName
    streetNumber
    zipCode
    city
    countrySubdivision
    country
    poBox
    phone
`;

export const FRAMEWORK_CONTRACT_MASTERDATA_ENTITY = `
    ${BASE_MASTERDATA_ENTITY}
    clientId
    clientName
    contractId1
    contractId2
    contractName
`;

export const CONTACT_MASTERDATA_ENTITY = `
    ${BASE_MASTERDATA_ENTITY}
    contactId
    phone
    mobilePhone
    email
    fullName
    firstName
    lastName
`;

export const ORDER_MATCHING_MASTERDATA_ENTITY = `
    ${BASE_MASTERDATA_ENTITY}
    orderNumber
    externalReference
    orderDate
    deliveryAddress
    receiverNumber
    positionNumber
    articleId1
    articleId2
    articleId3
    articleDescription
    articlePrice
    quantity
    totalPrice
    deliveryDate
    unit
    iban
    swift
    account
    paymentTermsType
    paymentTermsDiscount
    paymentTermsDueDays
`;

export const OFFER_MATCHING_MASTERDATA_ENTITY = `
    ${BASE_MASTERDATA_ENTITY}
    offerNumber
    positionNumber
    articleId1
    articleId2
    articleId3
    articleDescription
    quantity
    unit
`;

// Learned mappings, browsable in the explorer like the entities above; ids first, meta fields last.
// They are queried in their own document (SEARCH_MAPPINGS): names shared with the entity types above
// (clientId, targetUnit) differ in nullability, which one selection set does not allow.
const MAPPING_ENTITY_META = `
    partitionId
    highConfidence
    isIgnored
    createdAt
    recordInfo
`;

export const CLIENT_MAPPING_ENTITY = `
    id
    clientId
    sourceClientId
    senderName
    senderAddress
    invoiceReceiverName
    invoiceAddress
    deliveryReceiverName
    deliveryAddress
    contactName
    ${MAPPING_ENTITY_META}
`;

export const CONTACT_MAPPING_ENTITY = `
    id
    targetContactId
    targetContactName
    targetContactEmail
    targetContactPhone
    sourceContactName
    sourceContactEmail
    sourceContactPhone
    ${MAPPING_ENTITY_META}
`;

export const ARTICLE_MAPPING_ENTITY = `
    id
    targetArticleNumber
    targetUnit
    ean
    supplierArticleNumber
    debtorArticleNumber
    articleDescription
    unitPrice
    unit
    ${MAPPING_ENTITY_META}
`;

export const ADDRESS_MAPPING_ENTITY = `
    id
    targetAddressId
    targetAddressId2
    targetName
    targetCompanyName
    targetStreetAndNr
    targetMisc
    targetPostcode
    targetCity
    targetCountry
    targetEmail
    targetPhone
    sourceAddressId
    sourceName
    sourceCompanyName
    sourceStreetAndNr
    sourceMisc
    sourcePostcode
    sourceCity
    sourceCountry
    sourceEmail
    sourcePhone
    ${MAPPING_ENTITY_META}
`;

export const VAT_ID_CHECK_MAPPING_ENTITY = `
    id
    vatId
    clientName
    clientAddress
    ${MAPPING_ENTITY_META}
`;

export const POLYMORPHIC_MASTERDATA_ENTITY = `
    ... on ClientLookupType {
        ${CLIENT_MASTERDATA_ENTITY}
    }
    ... on ArticleLookupType {
        ${ARTICLE_MASTERDATA_ENTITY}
    }
    ... on AddressLookupType {
        ${ADDRESS_MASTERDATA_ENTITY}
    }
    ... on FrameworkContractLookupType {
        ${FRAMEWORK_CONTRACT_MASTERDATA_ENTITY}
    }
    ... on ContactLookupType {
        ${CONTACT_MASTERDATA_ENTITY}
    }
    ... on ConversionFactorLookupType {
        ${CONVERSION_FACTOR_MASTERDATA_ENTITY}
    }
    ... on OrderMatchingLookupType {
        ${ORDER_MATCHING_MASTERDATA_ENTITY}
    }
    ... on OfferMatchingLookupType {
        ${OFFER_MATCHING_MASTERDATA_ENTITY}
    }
`;

export const POLYMORPHIC_MAPPING_ENTITY = `
    ... on ClientMappingType {
        ${CLIENT_MAPPING_ENTITY}
    }
    ... on ContactMappingType {
        ${CONTACT_MAPPING_ENTITY}
    }
    ... on ArticleMappingType {
        ${ARTICLE_MAPPING_ENTITY}
    }
    ... on AddressMappingType {
        ${ADDRESS_MAPPING_ENTITY}
    }
    ... on VatIdCheckMappingType {
        ${VAT_ID_CHECK_MAPPING_ENTITY}
    }
`;

// Searchable fields must be included in the response types so that we can display their value. We also want to
// fetch all properties here needed for a bulk-update of related fields (i.e. the full entity). These are included
// as `_lookupEntity` in the update payload.
// The backend still uses _lookup_id to re-do the lookup, _lookupEntity is sent in case the
// backend should not refetch again.
export const SEARCH_MASTERDATA_ENTITY_FIELD = gql`
    query MasterdataEntityFieldAssistanceSearch(
        $lookupDefinitionId: UUID!
        $searchTerm: String!
        $targetField: String!
        $partitionId: String
        $from: Int
        $size: Int
        $kwargs: GenericScalar
    ) {
        assistanceSearchByField(
            lookupDefinitionId: $lookupDefinitionId
            searchTerm: $searchTerm
            targetField: $targetField
            partitionId: $partitionId
            From: $from
            size: $size
            kwargs: $kwargs
        ) {
            edges {
                node {
                    ${POLYMORPHIC_MASTERDATA_ENTITY}
                }
            }
        }
    }
`;

export const SEARCH_MASTERDATA = gql`
    query MasterdataEntityAssistanceSearch(
        $lookupDefinitionId: UUID!
        $from: Int
        $size: Int
        $searchFields: [SearchFieldInput!]  # {name: String!, value: String!}[]
    ) {
        assistanceSearch(
            lookupDefinitionId: $lookupDefinitionId
            From: $from
            size: $size
            searchFields: $searchFields
        ) {
            edges {
                node {
                    ${POLYMORPHIC_MASTERDATA_ENTITY}
                }
            }
            searchableFields
        }
    }
`;

// Same operation name as SEARCH_MASTERDATA, the gateway routes by it
export const SEARCH_MAPPINGS = gql`
    query MasterdataEntityAssistanceSearch(
        $lookupDefinitionId: UUID!
        $from: Int
        $size: Int
        $searchFields: [SearchFieldInput!]
    ) {
        assistanceSearch(
            lookupDefinitionId: $lookupDefinitionId
            From: $from
            size: $size
            searchFields: $searchFields
        ) {
            edges {
                node {
                    ${POLYMORPHIC_MAPPING_ENTITY}
                }
            }
            searchableFields
        }
    }
`;

export const GET_LOOKUP_DEFINITIONS = gql`
    query LookupDefinitions {
        lookupDefinitions {
            id
            name
            lookupType
            djangoAdminUrl
            entryCount
        }
    }
`;

// The operation names of the following lookup definition mutations must be listed in the backend's
// gateway allow-list (outsmart-backend gateway/middleware.py) to be proxied to the metadata-lookup service.
export const CREATE_LOOKUP_DEFINITION = gql`
    mutation CreateLookupDefinition($customerId: UUID!, $lookupType: LookupTypes!, $name: String) {
        createLookupDefinitionMutation(customerId: $customerId, lookupType: $lookupType, name: $name) {
            lookupDefinition {
                id
                name
                lookupType
            }
        }
    }
`;

export const UPDATE_LOOKUP_DEFINITION = gql`
    mutation UpdateLookupDefinition($lookupDefinitionId: UUID!, $name: String) {
        updateLookupDefinitionMutation(lookupDefinitionId: $lookupDefinitionId, name: $name) {
            lookupDefinition {
                id
                name
                lookupType
            }
        }
    }
`;

export const DELETE_LOOKUP_DEFINITION = gql`
    mutation DeleteLookupDefinition($lookupDefinitionId: UUID!) {
        deleteLookupDefinitionMutation(lookupDefinitionId: $lookupDefinitionId) {
            success
        }
    }
`;

// The operation names must match the gateway allow-list; the mutation field names are irregular
// (VAT ID check), so both are passed explicitly.
const buildDeleteMappingsMutation = (operationName: string, mutationName: string) => gql`
    mutation ${operationName}($lookupDefinitionId: UUID!, $beforeDate: Date!) {
        ${mutationName}(lookupDefinitionId: $lookupDefinitionId, beforeDate: $beforeDate) {
            deletedCount
        }
    }
`;

export const DELETE_MAPPINGS_BY_LOOKUP_TYPE = {
    CLIENT_MAPPING_LOOKUP: buildDeleteMappingsMutation('DeleteClientMapping', 'deleteClientMappingMutation'),
    CONTACT_MAPPING_LOOKUP: buildDeleteMappingsMutation('DeleteContactMapping', 'deleteContactMappingMutation'),
    ARTICLE_MAPPING_LOOKUP: buildDeleteMappingsMutation('DeleteArticleMapping', 'deleteArticleMappingMutation'),
    ADDRESS_MAPPING_LOOKUP: buildDeleteMappingsMutation('DeleteAddressMapping', 'deleteAddressMappingMutation'),
    VAT_ID_CHECK_MAPPING_LOOKUP: buildDeleteMappingsMutation('DeleteVatIdMapping', 'deleteVatIdCheckMappingMutation'),
};

const CHANNEL_USAGE_MASTER_DATA_CONFIG = `
    masterDataConfig {
        clientLookupDefinitionId
        receiverLookupDefinitionId
        contactLookupDefinitionId
        clientInternalContactLookupDefinitionId
        emailLookupDefinitionId
        articleLookupDefinitionId
        debitorArticleLookupDefinitionId
        conversionFactorLookupDefinitionId
        frameworkContractLookupDefinitionId
        invoiceAddressLookupDefinitionId
        deliveryAddressLookupDefinitionId
        clientMappingLookupDefinitionId
        contactMappingLookupDefinitionId
        articleMappingLookupDefinitionId
        vatIdCheckMappingLookupDefinitionId
        invoiceAddressMappingLookupDefinitionId
        deliveryAddressMappingLookupDefinitionId
        orderMatchingLookupDefinitionId
        offerMatchingLookupDefinitionId
    }
`;

// One entry per document product wired into the master data hub, in navigation (document type)
// order. The channel usage query and the per-product update mutations are derived from this list.
export const CHANNEL_PRODUCTS: { productKey: string; queryField: string; updateMutation: string }[] = [
    { productKey: 'rfqs', queryField: 'rfqProcessingChannels', updateMutation: 'updateRfqProcessingChannel' },
    { productKey: 'orders', queryField: 'orderProcessingChannels', updateMutation: 'updateOrderProcessingChannel' },
    {
        productKey: 'orderConfirmations',
        queryField: 'orderConfirmationProcessingChannels',
        updateMutation: 'updateOrderConfirmationProcessingChannel',
    },
    {
        productKey: 'invoices',
        queryField: 'invoiceProcessingChannels',
        updateMutation: 'updateInvoiceProcessingChannel',
    },
    {
        productKey: 'propertyBills',
        queryField: 'propertyBillProcessingChannels',
        updateMutation: 'updatePropertyBillProcessingChannel',
    },
    {
        productKey: 'deliveryNotes',
        queryField: 'deliveryNoteProcessingChannels',
        updateMutation: 'updateDeliveryNoteProcessingChannel',
    },
    {
        productKey: 'listOfServices',
        queryField: 'listOfServicesProcessingChannels',
        updateMutation: 'updateListOfServicesProcessingChannel',
    },
    {
        productKey: 'universal',
        queryField: 'universalProcessingChannels',
        updateMutation: 'updateUniversalProcessingChannel',
    },
];

// Resolved by the main backend (must NOT be part of the gateway allow-list): used to derive which
// channels reference a lookup definition, since there is no reverse lookup on the metadata service.
export const GET_MASTER_DATA_CHANNEL_USAGE = gql`
    query MasterDataChannelUsage {
        ${CHANNEL_PRODUCTS.map(
            ({ queryField }) => `${queryField} {
            id
            name
            ${CHANNEL_USAGE_MASTER_DATA_CONFIG}
        }`
        ).join('\n\n        ')}
    }
`;

const buildUpdateMasterDataConfigMutation = (mutationName: string) => gql`
    mutation UpdateChannelMasterDataConfig($channelId: UUID!, $masterDataConfig: UpdateMasterDataConfigInput) {
        ${mutationName}(channelId: $channelId, masterDataConfig: $masterDataConfig) {
            success
        }
    }
`;

// Resolved by the main backend (like GET_MASTER_DATA_CHANNEL_USAGE): partial updates of a channel's
// masterDataConfig, keyed by the product keys used in useLookupDefinitionChannelUsage
export const UPDATE_MASTER_DATA_CONFIG_BY_PRODUCT = Object.fromEntries(
    CHANNEL_PRODUCTS.map(({ productKey, updateMutation }) => [
        productKey,
        buildUpdateMasterDataConfigMutation(updateMutation),
    ])
);

const DATA_IMPORT_CONFIG_FRAGMENT = `
    id
    lookupDefinitionId
    transformerConfig {
        fileNamePatterns
        columns
        fileReaderKwargs {
            delimiter
            encoding
            quotechar
            escapechar
        }
        rawConfig
    }
`;

export const GET_DATA_IMPORT_CONFIG = gql`
    query DataImportConfig($lookupDefinitionId: UUID!) {
        dataImportConfig(lookupDefinitionId: $lookupDefinitionId) {
            ${DATA_IMPORT_CONFIG_FRAGMENT}
            
            fileNames
        }
    }
`;

export const UPDATE_DATA_IMPORT_CONFIG_RAW = gql`
    mutation UpdateDataImportConfigRaw($lookupDefinitionId: UUID!, $rawTransformerConfig: JSONString!) {
        updateDataImportConfigRaw(lookupDefinitionId: $lookupDefinitionId, rawTransformerConfig: $rawTransformerConfig) {
            dataImportConfig {
                ${DATA_IMPORT_CONFIG_FRAGMENT}
            }
        }
    }
`;

export const DRY_RUN_DATA_IMPORT = gql`
    mutation DryRunDataImport(
        $lookupDefinitionId: UUID!
        $maxRows: Int
        $transformerConfig: JSONString
        $sftpFiles: [DryRunSftpFileInput!]
        $files: [DryRunUploadedFileInput!]
    ) {
        dryRunDataImport(
            lookupDefinitionId: $lookupDefinitionId
            maxRows: $maxRows
            transformerConfig: $transformerConfig
            sftpFiles: $sftpFiles
            files: $files
        ) {
            success
            transformedRowsCount
            sampleRows
            detectedTransformerConfig
            logs
            error
            errorTraceback
        }
    }
`;

export const GET_SFTP_DIRECTORY = gql`
    query SftpDirectory($lookupDefinitionId: UUID!, $path: String, $fileNamePatterns: [String!]) {
        sftpDirectory(lookupDefinitionId: $lookupDefinitionId, path: $path, fileNamePatterns: $fileNamePatterns) {
            path
            entries {
                path
                name
                isDirectory
                size
                modifiedAt
                matchingPattern
            }
        }
    }
`;

export const IMPORT_LATEST_SFTP_FILES = gql`
    mutation ImportLatestSftpFiles($lookupDefinitionId: UUID!) {
        importLatestSftpFiles(lookupDefinitionId: $lookupDefinitionId) {
            dataImportRun {
                id
            }
            fileNames
        }
    }
`;
